/* eslint-disable @shopliftr/angular/dialog-service-conventions -- Notification service follows its own pattern */
import {
    Observable, of, Subject
} from "rxjs";
import {
    map, tap
} from "rxjs/operators";

import {
    DestroyRef,
    inject,
    Injectable, OnDestroy
} from "@angular/core";

import { NotificationType } from "../enums/notification-type.enum";

import { INotification } from "../interfaces/notification";
import {
    IConfirmationDialogData
} from "../interfaces/confirmation-dialog-data";

import { InvalidParametersException } from "../../exceptions/invalid-parameters-exception";

import { ConfirmationDialogComponent } from "../dialogs/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import {
    MatSnackBarConfig, MatSnackBar
} from "@angular/material/snack-bar";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    EnumUtil, StringUtil
} from "@shopliftr/common-js/data";
import { RxjsUtil } from "../../utils/rxjs-util";

/**
 * Type used as a parameter for the displayConfirmation$ function
 * It contains optional values for every field in IConfirmationDialogData except for "message"
 */
export type ConfirmationOptions = Partial<Omit<IConfirmationDialogData, "message">>;

/**
 * Type used as a parameter for the displayNotification function
 * It omits the "ref" field from the INotification structure since it only used internally
 */
export type NotificationOptions = Omit<INotification, "ref">;

/**
 * All messages displayed to the user are handled by this service
 *
 * If you add, remove, or change the interface of any public functions
 * you must update the NotificationServiceHelper.getNotificationServiceSpyObj
 * or you will break a lot of tests.
 *
 */
@Injectable(
    {
        providedIn: "root"
    }
)
export class NotificationService implements OnDestroy {

    /**
     * Default duration for non-persistent snack bars
     */
    static readonly DEFAULT_DURATION = 5000;

    private _notificationQueue: Array<INotification> = [];

    private _activeNotification: INotification;

    private _persistentNotification: INotification;

    private _persistentDismissed$: Subject<void>;

    /**
     * Determines if the notification is open
     */
    get isNotificationOpen(): boolean {

        return !!this._activeNotification;
    }

    /**
     * Determines if a persistent notification is currently displayed
     */
    get isPersistentNotificationOpen(): boolean {

        return !!this._activeNotification?.persistent;
    }

    /**
     * Observable that completes when the persistent observable is dismissed permanently
     */
    get persistentDismissed$(): Observable<void> {

        return this._persistentDismissed$?.asObservable() ?? of(undefined);
    }

    /**
     * Observable that completes when the current notification is dismissed
     *
     * If it is a persistent notification it will complete any time that notification is subsumed by
     * another. Use persistentDismissed$ for permanent dismissal in that case.
     */
    get activeNotificationDismissed$(): Observable<void> {

        return this._activeNotification?.ref?.afterDismissed().pipe(RxjsUtil.mapToVoid()) ?? of(undefined);
    }

    /**
     * The reference for when this service is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    private readonly _dialog = inject(MatDialog);

    private readonly _snackBar = inject(MatSnackBar);

    ngOnDestroy(): void {

        this.dismissAll();
    }

    dismissPersistent(): void {

        if (this._activeNotification?.persistent) {
            this.dismiss();
        }
        else {
            this._onPersistentDismiss();
        }
    }

    dismiss(): void {

        this._activeNotification?.ref?.dismissWithAction();
    }

    dismissAll(): void {

        this._notificationQueue = [];
        this.dismissPersistent();
        this.dismiss();
    }

    /**
     * Displays the correct message type based on the notification details
     *
     * For persistent messages, after calling this function use the persistentDismissed$ property on this service
     * to determine if the user dismisses the notification
     *
     * ```
     * notificationService.displayNotification(persistentNotification);
     * notificationService.persistentDismissed$.subscribe(() => ...);
     * ```
     */
    displayNotification(notification: NotificationOptions): void {

        if (StringUtil.emptyString(notification.message, true)) {
            throw new InvalidParametersException("message is required");
        }

        if (!EnumUtil.hasValue(NotificationType, notification.type)) {
            throw new InvalidParametersException(`unrecognized type ${notification.type}`);
        }

        if (notification.persistent && notification.duration) {
            throw new InvalidParametersException("Duration cannot be set on a persistent notification");
        }

        let snackbarNotification: INotification = {
            ...notification
        };

        if (snackbarNotification.persistent) {
            // Clear any existing persistent notification
            this.dismissPersistent();

            this._persistentDismissed$ = new Subject();
            this._persistentNotification = {
                ...snackbarNotification
            };
            if (!this._activeNotification) {
                this._openSnackbar(snackbarNotification);
            }
        }
        else if (this._activeNotification) {
            this._notificationQueue.push(notification);
            if (this._activeNotification.persistent) {
                this._activeNotification.ref.dismiss();
            }
        }
        else {
            this._openSnackbar(snackbarNotification);

        }
    }

    /**
     * Display warning message to the user
     */
    displayWarningMessage(message: string, action?: string, duration?: number): void {

        this.displayNotification({
            type: NotificationType.Warning,
            message: message,
            action: action,
            duration: duration
        });
    }

    /**
     * Display an error message to the user
     */
    displayErrorMessage(message: string, action?: string, duration?: number): void {

        this.displayNotification({
            type: NotificationType.Error,
            message: message,
            action: action,
            duration: duration
        });
    }

    /**
     * Display an error message for the provided exception to the user
     */
    displayException(exception: Error, messagePrefix?: string, action?: string, duration?: number): void {

        let message = exception.message;

        if (messagePrefix) {
            message = `${messagePrefix} ${message}`;
        }

        this.displayErrorMessage(message, action, duration);
    }

    /**
     * Display a notification with an indefinite duration
     *
     * If a persistent message is already open then this one will replace it.
     */
    displayPersistentMessage$(message: string, action?: string, type?: NotificationType): Observable<void> {

        this.displayNotification({
            type: type ?? NotificationType.Success,
            message: message,
            action: action,
            persistent: true
        });

        return this.persistentDismissed$;
    }

    /**
     * Display a success message to the user
     */
    displaySuccessMessage(message: string, action?: string, duration?: number): void {

        this.displayNotification({
            type: NotificationType.Success,
            message: message,
            action: action,
            duration: duration
        });
    }

    /**
     * Prompt the user to confirm an action with optional list items.
     *
     * Validates the message and any provided list items, then opens a confirmation dialog.
     * The dialog cannot be closed by clicking outside. Emits true if the user confirms,
     * false otherwise.
     *
     * @Returns and Observable which will emit a boolean for the user. It will never error. It will complete after it emits.
     */
    displayConfirmation$(message: string, options?: ConfirmationOptions): Observable<boolean> {

        // Validate that the message is provided and non-empty
        if (StringUtil.emptyString(message, true)) {
            throw new InvalidParametersException("Message is required and must be non-empty");
        }

        // Validate listItems if provided in options
        options?.listItems?.forEach((item) => {
            if (StringUtil.emptyString(item?.text, true)) {
                throw new InvalidParametersException("All list items must have a text property");
            }
        });

        // Prepare dialog data using provided message and options, with defaults for button texts
        const dialogData: IConfirmationDialogData = {
            message: message,
            title: options?.title,
            confirmationButtonText: options?.confirmationButtonText ?? "Yes",
            cancelButtonText: options?.cancelButtonText ?? "No",
            listItems: options?.listItems
        };

        // Open the confirmation dialog with the prepared data and disable closing by clicking outside
        const dialogRef = this._dialog.open(ConfirmationDialogComponent, {
            data: dialogData,
            disableClose: true
        });

        // Return an observable that emits true if confirmed, false otherwise
        return dialogRef.afterClosed().pipe(
            map((confirm) => {
                return !!confirm;
            })
        );
    }

    /**
     * Generate the MatSnackBarConfig for the notification to be displayed, setting any missing fields to the default.
     */
    private _generateSnackBarConfig(notification: INotification): MatSnackBarConfig {

        const config: MatSnackBarConfig = {};

        if (!notification.persistent) {
            config.duration = notification.duration ?? NotificationService.DEFAULT_DURATION;
        }

        switch (notification.type) {
            case NotificationType.Warning:
                config.panelClass = "mgd-snack-bar-warning";
                break;
            case NotificationType.Error:
                config.panelClass = "mgd-snack-bar-error";
                break;
        }
        return config;
    }

    private _onPersistentDismiss(): void {

        this._persistentNotification = undefined;
        this._persistentDismissed$?.next();
        this._persistentDismissed$?.complete();
        this._persistentDismissed$ = undefined;

    }

    private _openSnackbar(notification: INotification): void {

        const config = this._generateSnackBarConfig(notification);
        notification.action = notification.action ?? "OK";
        const ref = this._snackBar.open(notification.message, notification.action, config);
        this._activeNotification = {
            ...notification,
            ref: ref
        };

        let dismissed$ = ref.afterDismissed()
            .pipe(
                tap(() => this._activeNotification = undefined),
                takeUntilDestroyed(this._destroyRef)
            );
        if (notification.persistent) {
            dismissed$ = dismissed$.pipe(tap((value) => {

                if (value.dismissedByAction) {
                    // Permanently dismiss the persistent notification
                    this._onPersistentDismiss();
                }
            }));
        }
        dismissed$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {

                const nextNotification = this._notificationQueue.pop() ?? this._persistentNotification;

                if (nextNotification) {
                    this._openSnackbar(nextNotification);
                }
            });
    }
}
